import React, { useState } from 'react';
import { WorkflowGraphEditor } from './WorkflowGraphEditor.jsx';

/**
 * Read-only modal for visualising a Drupal workflow attached to a task type.
 *
 * Props:
 *   isOpen        boolean  — controls visibility
 *   onClose       () => void
 *   workflowId    string   — Drupal workflow machine name (e.g. 'tma_evolution')
 *   workflowLabel string   — human label shown in the modal header
 *   canEdit       boolean  — when true, shows an "Ouvrir l'éditeur" button
 *   onReassign    function — when provided, shows a "Changer de flux" button
 *   onUnassign    function — when provided, shows a "Retirer le flux" button
 */
const WorkflowViewModal = ({ isOpen, onClose, workflowId, workflowLabel, canEdit = false, onReassign, onUnassign }) => {
  const [confirmingUnassign, setConfirmingUnassign] = useState(false);

  if (!isOpen || !workflowId) return null;

  const handleOpenEditor = () => {
    window.open(`${window.location.origin}/workflow/edit/${encodeURIComponent(workflowId)}`, '_blank', 'noopener');
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-stretch justify-center bg-black/60 backdrop-blur-sm">
      <div className="flex flex-col w-full max-w-6xl my-6 mx-4 bg-white rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50 shrink-0">
          <div className="flex items-center gap-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-iris-600"
            >
              <circle cx="18" cy="18" r="3" />
              <circle cx="6" cy="6" r="3" />
              <path d="M13 6h3a2 2 0 0 1 2 2v7" />
              <line x1="6" y1="9" x2="6" y2="21" />
            </svg>
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                {workflowLabel || workflowId}
              </h2>
              <p className="text-xs text-gray-400 font-mono">{workflowId}</p>
            </div>
            <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500 border border-gray-200">
              Lecture seule
            </span>
          </div>

          <div className="flex items-center gap-2">
            {onReassign && (
              <button
                onClick={() => { setConfirmingUnassign(false); onClose(); onReassign(); }}
                className="btn btn-accent flex items-center gap-1.5"
                title="Assigner un autre flux à ce type de tâche"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="17 1 21 5 17 9" />
                  <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                  <polyline points="7 23 3 19 7 15" />
                  <path d="M21 13v2a4 4 0 0 1-4 4H3" />
                </svg>
                Changer de flux
              </button>
            )}
            {onUnassign && (
              confirmingUnassign ? (
                <span className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-500 pr-1">Retirer ce flux ?</span>
                  <button
                    onClick={() => { setConfirmingUnassign(false); onClose(); onUnassign(); }}
                    className="btn btn-danger btn-sm"
                  >
                    Confirmer
                  </button>
                  <button
                    onClick={() => setConfirmingUnassign(false)}
                    className="btn btn-secondary btn-sm"
                  >
                    Annuler
                  </button>
                </span>
              ) : (
                <button
                  onClick={() => setConfirmingUnassign(true)}
                  className="btn btn-danger flex items-center gap-1.5"
                  title="Retirer le flux assigné à ce type de tâche"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                  Retirer le flux
                </button>
              )
            )}
            {canEdit ? (
              <button
                onClick={handleOpenEditor}
                className="btn btn-primary flex items-center gap-1.5"
                title="Ouvrir l'éditeur de workflow dans un nouvel onglet"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                Ouvrir l'éditeur
              </button>
            ) : (
              <span
                title="Nécessite le privilège « administer workflows »"
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-400 bg-gray-100 rounded-lg cursor-not-allowed select-none"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                Ouvrir l'éditeur
              </span>
            )}

            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Fermer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Graph — fills remaining space */}
        <div className="flex-1 min-h-0">
          <WorkflowGraphEditor
            props={{ workflowId, readOnly: true }}
          />
        </div>
      </div>
    </div>
  );
};

export default WorkflowViewModal;
